const BURGUNDY = '#8B1A1A'
const GOLD = '#B8860B'
const BLUE = '#4a9eff'
const GREEN = '#10b981'

const S = {
  shell: { color: '#e0e0e0', fontFamily: "'DM Sans', sans-serif" },
  sectionLabel: { fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD, fontWeight: 600, marginBottom: '1.2rem' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' },
  card: { background: '#111', border: '1px solid #1e1e1e', borderRadius: '8px', padding: '1.25rem' },
  cardTitle: { fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: GOLD, fontWeight: 600, marginBottom: '1rem', paddingBottom: '0.6rem', borderBottom: '1px solid #1e1e1e' },
  entityBox: { background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: '6px', padding: '1rem', marginBottom: '0.75rem' },
  entityName: { fontSize: '0.95rem', fontWeight: 700, color: '#e0e0e0', marginBottom: '0.2rem' },
  entitySub: { fontSize: '0.78rem', color: '#bbb', marginBottom: '0.5rem' },
  entityBadge: { display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '3px', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginRight: '0.35rem', marginBottom: '0.35rem' },
  entityDetail: { fontSize: '0.78rem', color: '#aaa', lineHeight: 1.6, marginTop: '0.5rem' },
  arrow: { textAlign: 'center', color: BURGUNDY, fontSize: '1.2rem', margin: '0.25rem 0', padding: '0' },
  timelineItem: { display: 'flex', gap: '1rem', marginBottom: '1.25rem', alignItems: 'flex-start' },
  timelineDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, marginTop: '5px' },
  timelineYear: { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#bbb', minWidth: '38px', marginTop: '3px' },
  timelineContent: { flex: 1 },
  timelineTitle: { fontSize: '0.88rem', fontWeight: 600, color: '#d8d8d8', marginBottom: '0.25rem', lineHeight: 1.4 },
  timelineBody: { fontSize: '0.78rem', color: '#aaa', lineHeight: 1.6 },
  sourceNote: { fontSize: '0.72rem', color: '#bbb', borderTop: '1px solid #1a1a1a', paddingTop: '1rem', marginTop: '0.75rem' },
  redFlag: { background: '#1a0505', border: `1px solid ${BURGUNDY}`, borderLeft: `3px solid ${BURGUNDY}`, borderRadius: '4px', padding: '0.75rem 1rem', marginBottom: '0.5rem' },
  redFlagText: { fontSize: '0.8rem', color: '#d0c0c0', lineHeight: 1.5 },
  redFlagLabel: { fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: BURGUNDY, fontWeight: 700, marginBottom: '0.3rem' },
}

const CORPORATE_CHAIN = [
  {
    name: 'Cooke Inc.',
    sub: 'Privately held — Blacks Harbour, New Brunswick, Canada',
    badges: [{ label: 'Parent', color: '#2a1a1a', text: BURGUNDY }, { label: 'Canadian', color: '#1a1a2a', text: '#6699cc' }],
    detail: 'Largest privately held seafood company in North America. Annual revenue ~$3B+. Acquired Omega Protein in 2017 for $500M. Glenn Cooke, founder/CEO. No public securities filings.',
    flags: [],
  },
  {
    name: 'Omega Protein Corporation',
    sub: 'Wholly owned subsidiary of Cooke Inc. since 2017',
    badges: [{ label: 'Subsidiary', color: '#1a1200', text: GOLD }, { label: 'Former NYSE: OME', color: '#1a1a1a', text: '#888' }],
    detail: 'Controls ~80% of US Atlantic menhaden reduction capacity. Operates: Reedville VA (largest), Cameron LA, Moss Point MS, Abbeville LA, Morgan City LA. Previously traded publicly; taken private by Cooke.',
    flags: [
      { label: 'EPA Consent Order', text: '2018: Consent order for Clean Air Act violations at Reedville facility. $1.4M penalty + equipment upgrades.' },
      { label: 'OSHA violations', text: 'Multiple OSHA citations at Reedville 2015–2022 for process safety management deficiencies.' },
    ],
  },
  {
    name: 'Reedville Processing Facility',
    sub: 'Reedville, Virginia — Northern Neck',
    badges: [{ label: 'Primary Atlantic plant', color: '#0d1a0d', text: '#4a9eff' }],
    detail: 'Only Atlantic coast menhaden reduction plant. Capacity: ~400,000 MT/year. Employs ~400 workers. Reported wages: $17–18/hr. Directly fishes Chesapeake Bay under Bay cap allocation.',
    flags: [],
  },
]

const TIMELINE = [
  { year: '1940s', color: '#3a3a3a', title: 'Industry consolidation begins', body: 'Atlantic menhaden reduction industry consolidates from dozens of small operators to handful of large companies over post-WWII decades.' },
  { year: '1976', color: '#3a3a3a', title: 'Magnuson-Stevens Act', body: 'Federal fisheries management framework established. Menhaden management assigned to ASMFC as an interstate compact — outside MSA federal oversight.' },
  { year: '1994', color: GOLD, title: 'ASMFC menhaden management begins', body: 'Atlantic States Marine Fisheries Commission takes formal management authority. Industry representatives hold significant seats on management board.' },
  { year: '2005', color: GREEN, title: 'Maryland Bay moratorium', body: 'Maryland unilaterally bans reduction fishing in its Bay waters after concerns about ecosystem impacts. Virginia declines to follow.' },
  { year: '2012', color: GOLD, title: 'Coastwide cap + Bay cap established', body: 'ASMFC Addendum I sets 51,000 MT Bay cap and 153,000 MT coastwide quota. Environmental groups call it insufficient; industry calls it unnecessary.' },
  { year: '2013', color: BURGUNDY, title: 'Omega Protein: first-ever overfishing finding', body: "ASMFC finds menhaden overfished for the first time in the stock's management history. Triggers marginal quota reduction." },
  { year: '2017', color: BURGUNDY, title: 'Cooke Inc. acquires Omega Protein for $500M', body: 'Canadian company Cooke Inc. acquires Omega Protein, consolidating ~80% of US reduction capacity under foreign private ownership. No CFIUS review required for fisheries.' },
  { year: '2018', color: BURGUNDY, title: 'EPA Consent Order — Reedville', body: 'EPA issues consent order to Omega Protein for Clean Air Act violations at Reedville. $1.4M penalty. Facility had exceeded hydrogen sulfide emission limits.' },
  { year: '2020', color: GOLD, title: 'Ecosystem reference points adopted (partially)', body: 'ASMFC adopts "ecological reference points" framework in principle — first time in menhaden management history. Implementation remains contested.' },
  { year: '2022', color: GOLD, title: 'ASMFC Addendum III: modest quota increase', body: "Despite ecosystem concerns, ASMFC increases coastwide quota. Industry's argument that stock is healthy prevails over precautionary advocacy." },
  { year: '2023', color: BURGUNDY, title: 'Stock assessment: concerns flagged', body: 'Independent scientists flag concerns about Chesapeake Bay menhaden specifically, even as coastwide stock is deemed "not overfished." Bay-specific metrics show continued depletion relative to ecosystem targets.' },
  { year: '2025', color: BLUE, title: 'Virginia legislative session — reform attempts', body: 'Virginia advocates push for Bay cap reduction and state-level restrictions. Bills introduced but die in committee amid industry lobbying.' },
]

export default function Investigation() {
  return (
    <div style={S.shell}>
      <div style={S.sectionLabel}>Investigation</div>

      <div style={S.row}>
        <div style={S.card}>
          <div style={S.cardTitle}>Corporate Ownership Chain</div>
          {CORPORATE_CHAIN.map((entity, i) => (
            <div key={entity.name}>
              <div style={S.entityBox}>
                <div style={S.entityName}>{entity.name}</div>
                <div style={S.entitySub}>{entity.sub}</div>
                <div>
                  {entity.badges.map(b => (
                    <span key={b.label} style={{ ...S.entityBadge, background: b.color, color: b.text, border: `1px solid ${b.text}40` }}>
                      {b.label}
                    </span>
                  ))}
                </div>
                <div style={S.entityDetail}>{entity.detail}</div>
                {entity.flags.map(f => (
                  <div key={f.label} style={{ ...S.redFlag, marginTop: '0.5rem' }}>
                    <div style={S.redFlagLabel}>{f.label}</div>
                    <div style={S.redFlagText}>{f.text}</div>
                  </div>
                ))}
              </div>
              {i < CORPORATE_CHAIN.length - 1 && (
                <div style={S.arrow}>↓</div>
              )}
            </div>
          ))}
          <div style={S.sourceNote}>Sources: SEC EDGAR (pre-2017 filings), EPA enforcement database, OSHA inspection records, ASMFC meeting transcripts</div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>Regulatory Timeline</div>
          {TIMELINE.map((item, i) => (
            <div key={i} style={S.timelineItem}>
              <div style={{ ...S.timelineDot, background: item.color }} />
              <div style={S.timelineYear}>{item.year}</div>
              <div style={S.timelineContent}>
                <div style={S.timelineTitle}>{item.title}</div>
                <div style={S.timelineBody}>{item.body}</div>
              </div>
            </div>
          ))}
          <div style={S.sourceNote}>Sources: ASMFC meeting records, EPA enforcement database, congressional records, NOAA stock assessments, Virginia General Assembly records</div>
        </div>
      </div>
    </div>
  )
}
